import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, AlertCircle, AlertTriangle, Database, HardDrive, Server, CheckSquare, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface HealthCheck {
  overallStatus: "healthy" | "warning" | "unhealthy";
  appVersion: string;
  environment: string;
  checks: {
    db: {
      status: "ok" | "error";
      message: string;
      latency?: number;
    };
    storage: {
      status: "ok" | "error";
      message: string;
      latency?: number;
    };
    env: {
      status: "ok" | "warning";
      messages: string[];
    };
  };
  timestamp: string;
}

// Simple go-live checklist items
const goLiveChecklist = [
  { id: "db", label: "Database connection verified", icon: Database },
  { id: "storage", label: "Storage/uploads verified", icon: HardDrive },
  { id: "jwt", label: "Strong JWT_SECRET set", icon: Server },
  { id: "env", label: "NODE_ENV=production", icon: Server },
  { id: "https", label: "HTTPS enabled", icon: Server },
  { id: "backups", label: "DB backups configured", icon: HardDrive },
  { id: "email", label: "Email notifications tested", icon: Server },
  { id: "admins", label: "Admin users created", icon: CheckSquare },
  { id: "wells", label: "Wells & sites configured", icon: CheckSquare },
  { id: "data", label: "Historical data imported", icon: HardDrive },
  { id: "training", label: "Operators trained", icon: CheckCircle },
  { id: "offline", label: "Offline mode tested", icon: Server },
];

export default function AdminStatus() {
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [health, setHealth] = useState<HealthCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect non-admin users
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      setLocation("/");
    }
  }, [isAuthenticated, user, setLocation]);

  // Fetch health status
  const fetchHealthStatus = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch("/api/health");
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch health status");
      }
      
      setHealth(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch health status";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    if (isAuthenticated && user && user.role === 'admin') {
      fetchHealthStatus();
    }
  }, [isAuthenticated, user]);

  // Status badge styles
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "healthy":
        return "bg-green-100 text-green-800 border-green-300";
      case "warning":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "unhealthy":
        return "bg-red-100 text-red-800 border-red-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  // Check icon
  const getCheckIcon = (status: string) => {
    switch (status) {
      case "ok":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  // Show loading while checking auth
  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if not admin
  if (user.role !== 'admin') {
    return null; // Will redirect
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Status</h1>
          <p className="text-muted-foreground">Monitor application health and go-live readiness</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchHealthStatus}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
              Refreshing...
            </>
          ) : (
            "Refresh Status"
          )}
        </Button>
      </div>

      {error && (
        <Card className="border-red-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Error Loading Status
            </CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Overall Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Overall System Status
          </CardTitle>
          <CardDescription>
            {health && `Last updated: ${new Date(health.timestamp).toLocaleString()}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Checking system health...</p>
            </div>
          ) : health ? (
            <>
              <div className="flex items-center gap-3">
                <Badge variant="outline" className={getStatusBadge(health.overallStatus)}>
                  {health.overallStatus === "healthy" && <CheckCircle className="h-3 w-3 mr-1" />}
                  {health.overallStatus === "warning" && <AlertTriangle className="h-3 w-3 mr-1" />}
                  {health.overallStatus === "unhealthy" && <XCircle className="h-3 w-3 mr-1" />}
                  {health.overallStatus.toUpperCase()}
                </Badge>
                <div>
                  <p className="text-sm font-medium">Version {health.appVersion}</p>
                  <p className="text-xs text-muted-foreground">Environment: {health.environment}</p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Unable to load health data</p>
          )}
        </CardContent>
      </Card>

      {/* Checks Section */}
      {health && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Database Check */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Database className="h-4 w-4" />
                Database
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                {getCheckIcon(health.checks.db.status)}
                <span className="text-sm">{health.checks.db.message}</span>
              </div>
              {health.checks.db.latency && (
                <p className="text-xs text-muted-foreground">
                  Latency: {health.checks.db.latency}ms
                </p>
              )}
            </CardContent>
          </Card>

          {/* Storage Check */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HardDrive className="h-4 w-4" />
                Storage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                {getCheckIcon(health.checks.storage.status)}
                <span className="text-sm">{health.checks.storage.message}</span>
              </div>
              {health.checks.storage.latency && (
                <p className="text-xs text-muted-foreground">
                  Latency: {health.checks.storage.latency}ms
                </p>
              )}
            </CardContent>
          </Card>

          {/* Environment Warnings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Server className="h-4 w-4" />
                Environment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2">
                {getCheckIcon(health.checks.env.status)}
                <span className="text-sm capitalize">{health.checks.env.status}</span>
              </div>
              {health.checks.env.messages.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {health.checks.env.messages.map((msg, idx) => (
                    <li key={idx} className="text-xs text-muted-foreground flex items-start gap-1">
                      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span>{msg}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Go-Live Checklist */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5" />
            Go-Live Readiness Checklist
          </CardTitle>
          <CardDescription>
            Essential items to verify before deploying to production
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {goLiveChecklist.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors"
                >
                  <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span className="text-sm text-foreground">{item.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
